import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { Button, Dialog, DialogActions, DialogContent, TextField } from "@material-ui/core";

import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import { ModalTitle, FormField } from "../../components/QueueIntegrationModal/panelKit";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

// Simple "send this batch by WhatsApp" dialog: asks for a phone number and posts
// POST /hotspot/voucher-batches/:id/send { number }. Used by NewBatchModal right
// after a batch is created (batch is already known, no picker needed). The full
// picker + ticket-aware flow lives in components/SendVoucherDialog — kept
// separate here to avoid a circular import (SendVoucherDialog reuses
// NewBatchModal for its own "criar lote agora" action).
const SendBatchDialog = ({ open, batch, onClose }) => {
  const [number, setNumber] = useState("");
  const [sending, setSending] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      setNumber("");
      setSending(false);
    }
  }, [open, batch]);

  const digits = number.replace(/\D/g, "");
  const valid = digits.length >= 10 && digits.length <= 13;

  const handleSend = async () => {
    if (!batch?.id || !valid) return;
    setSending(true);
    try {
      await api.post(`/hotspot/voucher-batches/${batch.id}/send`, { number: digits });
      toast.success(i18n.t("hotspot.toasts.sent"));
      if (isMountedRef.current) onClose();
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setSending(false);
    }
  };

  const numberError = number.length > 0 && !valid;

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="xs">
      <ModalTitle title={i18n.t("hotspot.sendDialog.title")} subtitle={i18n.t("hotspot.sendDialog.help")} />
      <DialogContent>
        <FormField
          required
          label={i18n.t("hotspot.sendDialog.number")}
          htmlFor="hotspot-send-number"
          error={numberError ? i18n.t("hotspot.sendDialog.numberInvalid") : null}
        >
          <TextField
            id="hotspot-send-number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="51999998888"
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
            disabled={sending}
            error={numberError}
            inputProps={{ inputMode: "numeric" }}
          />
        </FormField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          {i18n.t("hotspot.buttons.cancel")}
        </Button>
        <ButtonWithSpinner
          onClick={handleSend}
          color="primary"
          variant="contained"
          loading={sending}
          disabled={sending || !valid}
        >
          {i18n.t("hotspot.sendDialog.send")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default SendBatchDialog;
